
import { GoogleGenAI, Type } from "@google/genai";
import { Player, NewsItem, VideoItem, Language, Game } from "../types";
import { getFromCache, saveToCache, dedupRequest } from "./storageService";
import { calculateTimeAgo } from "./formatService";
import { callGemini, callOpenAI } from "./aiCircuitBreaker";

const getLangName = (code: Language): string => {
  const map: Record<Language, string> = {
    en: 'English', 
    ko: 'Korean'
    // ja: 'Japanese',
    // es: 'Spanish',
    // fr: 'French'
  };
  return map[code] || 'English';
}

export const fetchFootballGames = async (lang: Language = 'en'): Promise<Game[]> => {
    return [];
};

export const predictPlayerTransfer = async (player: Player, lang: Language = 'en'): Promise<string> => {
  const cacheKey = `prediction_${player.id}_${lang}`;
  const cached = getFromCache<string>(cacheKey);
  if (cached) return cached;

  return dedupRequest(cacheKey, async () => {
    try {
      const langName = getLangName(lang);
      const prompt = `Transfer probability for ${player.name} (${player.team}). Stats: ${JSON.stringify(player.stats.slice(-1))}. Respond in ${langName}: probability % + 1-sentence reason.`;
      const result = await callGemini(async () => {
        const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
        const response = await ai.models.generateContent({ model: import.meta.env.VITE_GEMINI_MODEL, contents: prompt });
        return response.text || "Unable to predict.";
      });
      saveToCache(cacheKey, result);
      return result;
    } catch (error) { return "AI service unavailable."; }
  });
};

export interface MatchPredictionData {
    predicted_winner: string;
    predicted_score: string;
    win_probability: number;
    analysis_reason: string;
    analysis_reason_ko: string;
    // analysis_reason_ja?: string;
    // analysis_reason_es?: string;
    // analysis_reason_fr?: string;
}

const MATCH_PREDICT_PROMPT = (homeTeam: string, awayTeam: string, newsContext?: string) =>
    `Match: ${homeTeam} vs ${awayTeam}.${newsContext ? `\n[Context]\n${newsContext}\n` : ''}
Output JSON:
- predicted_winner: likely winner or "Draw"
- predicted_score: e.g. "2:1"
- win_probability: 0-100
- analysis_reason: 2-paragraph English tactical analysis + up to 3 bullet points (use • ). Use \\n\\n between sections.
- analysis_reason_ko: exact Korean translation of analysis_reason.`;

const callOpenAIPrediction = async (prompt: string): Promise<MatchPredictionData> => {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    if (!apiKey) throw new Error('NO_OPENAI_KEY');

    const res = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
            model: 'gpt-4o-mini',
            messages: [{ role: 'user', content: prompt }],
            response_format: { type: 'json_object' },
        }),
    });

    if (!res.ok) throw new Error(`OPENAI_${res.status}`);
    const data = await res.json();
    const text = data.choices?.[0]?.message?.content || '{}';
    return JSON.parse(text) as MatchPredictionData;
};

export const predictMatchResult = async (homeTeam: string, awayTeam: string, newsContext?: string): Promise<MatchPredictionData | null> => {
    const cacheKey = `match_predict_json_v3_${homeTeam}_vs_${awayTeam}`;
    const cached = getFromCache<MatchPredictionData>(cacheKey);
    if (cached) return cached;

    return dedupRequest(cacheKey, async () => {
        const prompt = MATCH_PREDICT_PROMPT(homeTeam, awayTeam, newsContext);
        let geminiError: unknown = null;

        // 1차: Gemini 시도
        try {
            const result = await callGemini(async () => {
                const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
                const response = await ai.models.generateContent({
                    model: import.meta.env.VITE_GEMINI_MODEL,
                    contents: prompt,
                    config: {
                        responseMimeType: "application/json",
                        responseSchema: {
                            type: Type.OBJECT,
                            properties: {
                                predicted_winner: { type: Type.STRING },
                                predicted_score: { type: Type.STRING },
                                win_probability: { type: Type.NUMBER },
                                analysis_reason: { type: Type.STRING },
                                analysis_reason_ko: { type: Type.STRING },
                            },
                            required: ["predicted_winner", "predicted_score", "win_probability", "analysis_reason", "analysis_reason_ko"],
                        }
                    }
                });
                return JSON.parse(response.text || '{}') as MatchPredictionData;
            });
            if (result.predicted_winner) {
                saveToCache(cacheKey, result, 1000 * 60 * 60 * 12);
                return result;
            }
        } catch (error) {
            geminiError = error;
            console.warn("Gemini prediction failed, trying OpenAI fallback:", error);
        }

        // 2차: OpenAI gpt-4o-mini 폴백
        try {
            const result = await callOpenAI(() => callOpenAIPrediction(prompt));
            if (result.predicted_winner) {
                saveToCache(cacheKey, result, 1000 * 60 * 60 * 12);
                return result;
            }
        } catch (openaiError) {
            console.error("OpenAI fallback also failed:", openaiError);
            throw new Error('AI_BOTH_FAILED');
        }

        if (geminiError) throw new Error('AI_BOTH_FAILED');
        return null;
    });
};

export const fetchFootballNews = async (lang: Language = 'en', pageCursor?: string): Promise<{ news: NewsItem[], sources: any[], nextPage?: string }> => {
  const cacheKey = `news_data_io_${lang}_${pageCursor || 'first'}`;
  const cached = getFromCache<{ news: NewsItem[], sources: any[], nextPage?: string }>(cacheKey);
  if (cached) return cached;

  try {
    const API_KEY = 'pub_30f3898a578e40afbfb1064f7295bc95';
    const QUERY = 'soccer AND (transfer OR rumor OR "linked")';
    let url = `https://newsdata.io/api/1/latest?apikey=${API_KEY}&qInMeta=${encodeURIComponent(QUERY)}&image=1&category=sports&language=${lang}`;
    if (pageCursor) url += `&page=${pageCursor}`;

    const response = await fetch(url);
    const data = await response.json();
    if (data.status !== 'success' || !data.results) throw new Error('NewsData API failed');

    const news: NewsItem[] = data.results.map((article: any) => ({
        id: `nd-${article.article_id}`,
        title: article.title || 'No Title',
        summary: article.description?.substring(0, 150) + '...' || article.title,
        time: calculateTimeAgo(article.pubDate),
        url: article.link,
        image: article.image_url,
        source: article.source_id
    }));
    const uniqueSources = Array.from(new Set(data.results.map((a: any) => a.source_id))).map(id => ({ title: id, uri: '' })).slice(0, 5);
    const result = { news, sources: uniqueSources, nextPage: data.nextPage };
    saveToCache(cacheKey, result, 1000 * 60 * 15); 
    return result;
  } catch (error) { return { news: [], sources: [] }; }
};

export const expandNewsStory = async (newsItem: NewsItem, lang: Language = 'en'): Promise<string> => {
  const cacheKey = `news_expanded_${newsItem.id}_${lang}`;
  const cached = getFromCache<string>(cacheKey);
  if (cached) return cached;
  try {
     const text = await callGemini(async () => {
       const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
       const response = await ai.models.generateContent({
         model: import.meta.env.VITE_GEMINI_MODEL,
         contents: `Write a 120-word sports article in ${getLangName(lang)} about: "${newsItem.title}". Context: ${newsItem.summary.substring(0, 100)}.`
       });
       return response.text || "Content unavailable.";
     });
     saveToCache(cacheKey, text);
     return text;
  } catch (e) { return "Content unavailable."; }
};

export interface NewsTranslationData {
    title_en: string;
    // title_ja: string;
    // title_es: string;
    // title_fr: string;
    summary_en: string;
    // summary_ja: string;
    // summary_es: string;
    // summary_fr: string;
    content_en: string;
    // content_ja: string;
    // content_es: string;
    // content_fr: string;
}

export const translateNewsContent = async (title: string, summary: string, content: string): Promise<NewsTranslationData | null> => {
    try {
        const prompt = `Translate Korean football news to English. Title: ${title}\nSummary: ${summary.substring(0, 150)}\nContent: ${content.substring(0, 500)}`;
        return await callGemini(async () => {
            const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
            const response = await ai.models.generateContent({
                model: import.meta.env.VITE_GEMINI_MODEL,
                contents: prompt,
                config: {
                    responseMimeType: "application/json",
                    responseSchema: {
                        type: Type.OBJECT,
                        properties: {
                            title_en: { type: Type.STRING },
                            // title_ja: { type: Type.STRING },
                            // title_es: { type: Type.STRING },
                            // title_fr: { type: Type.STRING },
                            summary_en: { type: Type.STRING },
                            // summary_ja: { type: Type.STRING },
                            // summary_es: { type: Type.STRING },
                            // summary_fr: { type: Type.STRING },
                            content_en: { type: Type.STRING },
                            // content_ja: { type: Type.STRING },
                            // content_es: { type: Type.STRING },
                            // content_fr: { type: Type.STRING }
                        },
                        required: [
                            "title_en",
                            "summary_en",
                            "content_en",
                        ]
                        // required: [
                        //     "title_en", "title_ja", "title_es", "title_fr",
                        //     "summary_en", "summary_ja", "summary_es", "summary_fr",
                        //     "content_en", "content_ja", "content_es", "content_fr"
                        // ]
                    }
                }
            });
            return JSON.parse(response.text || '{}') as NewsTranslationData;
        });
    } catch (error) {
        console.error("News Translation Error:", error);
        return null;
    }
};

export interface TotoTitleTranslation {
    title_en: string;
    title_ko: string;
    // title_ja: string;
    // title_es: string;
    // title_fr: string;
}

export const translateTotoTitle = async (title: string): Promise<TotoTitleTranslation | null> => {
    try {
        const prompt = `Translate this football toto title to English and Korean: "${title}"`;
        return await callGemini(async () => {
            const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
            const response = await ai.models.generateContent({
                model: import.meta.env.VITE_GEMINI_MODEL,
                contents: prompt,
                config: {
                    responseMimeType: "application/json",
                    responseSchema: {
                        type: Type.OBJECT,
                        properties: {
                            title_en: { type: Type.STRING },
                            title_ko: { type: Type.STRING },
                            // title_ja: { type: Type.STRING },
                            // title_es: { type: Type.STRING },
                            // title_fr: { type: Type.STRING }
                        },
                        required: ["title_en", "title_ko"]
                        // required: ["title_en", "title_ko", "title_ja", "title_es", "title_fr"]
                    }
                }
            });
            return JSON.parse(response.text || '{}') as TotoTitleTranslation;
        });
    } catch (error) {
        console.error("Toto Title Translation Error:", error);
        return null;
    }
};

export interface QuizTranslationData {
    category: string;
    question_en: string;
    // question_ja: string;
    // question_es: string;
    // question_fr: string;
    options_en: string[];
    // options_ja: string[];
    // options_es: string[];
    // options_fr: string[];
    explanation_en: string;
    // explanation_ja: string;
    // explanation_es: string;
    // explanation_fr: string;
}

export const detectLanguage = (text: string): Language | 'emoji' => {
    // 이모티콘, 숫자, 공백, 문장 부호만 있는지 확인
    const onlyEmojiRegex = /^[\s\d\p{P}\p{S}\p{Emoji_Modifier_Base}\p{Emoji_Component}\p{Emoji_Presentation}\p{Emoji}\u200d]+$/u;
    if (onlyEmojiRegex.test(text)) return 'emoji';

    const koreanRegex = /[ㄱ-ㅎㅏ-ㅣ가-힣]/;
    const japaneseRegex = /[\u3040-\u309F\u30A0-\u30FF]/;
    const spanishRegex = /[áéíóúüñÁÉÍÓÚÜÑ]/;
    const frenchRegex = /[àâçéèêëîïôûùÀÂÇÉÈÊËÎÏÔÛÙ]/;

    if (koreanRegex.test(text)) return 'ko';
    // if (japaneseRegex.test(text)) return 'ja';
    // if (spanishRegex.test(text)) return 'es';
    // if (frenchRegex.test(text)) return 'fr';
    return 'en';
};

export const translateText = async (text: string, targetLang: Language): Promise<string | null> => {
    try {
        const langName = getLangName(targetLang);
        const prompt = `Translate to ${langName} (output only): ${text}`;
        return await callGemini(async () => {
            const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
            const response = await ai.models.generateContent({ model: import.meta.env.VITE_GEMINI_MODEL, contents: prompt });
            return response.text || null;
        });
    } catch (error) {
        console.error("Translation Error:", error);
        return null;
    }
};

export const translateQuizContent = async (category: string, question: string, options: string[], explanation: string): Promise<QuizTranslationData | null> => {
    try {
        const prompt = `Translate Korean football quiz to English. If category is "general", reclassify to one of: history|rules|players|tactics|competitions.\nCategory: ${category}\nQ: ${question}\nOptions: ${JSON.stringify(options)}\nExplanation: ${explanation}`;
        return await callGemini(async () => {
            const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
            const response = await ai.models.generateContent({
                model: import.meta.env.VITE_GEMINI_MODEL,
                contents: prompt,
                config: {
                    responseMimeType: "application/json",
                    responseSchema: {
                        type: Type.OBJECT,
                        properties: {
                            category: { type: Type.STRING },
                            question_en: { type: Type.STRING },
                            // question_ja: { type: Type.STRING },
                            // question_es: { type: Type.STRING },
                            // question_fr: { type: Type.STRING },
                            options_en: { type: Type.ARRAY, items: { type: Type.STRING } },
                            // options_ja: { type: Type.ARRAY, items: { type: Type.STRING } },
                            // options_es: { type: Type.ARRAY, items: { type: Type.STRING } },
                            // options_fr: { type: Type.ARRAY, items: { type: Type.STRING } },
                            explanation_en: { type: Type.STRING },
                            // explanation_ja: { type: Type.STRING },
                            // explanation_es: { type: Type.STRING },
                            // explanation_fr: { type: Type.STRING }
                        },
                        required: [
                            "category",
                            "question_en",
                            "options_en",
                            "explanation_en",
                        ]
                        // required: [
                        //     "category",
                        //     "question_en", "question_ja", "question_es", "question_fr",
                        //     "options_en", "options_ja", "options_es", "options_fr",
                        //     "explanation_en", "explanation_ja", "explanation_es", "explanation_fr"
                        // ]
                    }
                }
            });
            return JSON.parse(response.text || '{}') as QuizTranslationData;
        });
    } catch (error) {
        console.error("Quiz Translation Error:", error);
        return null;
    }
};
